import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { Transaction, UserModelData } from "../../../types";
import LoadingIndicator from "../../components/LoadingIndicator";
import FlatList from "../../components/FlatList";
import Text from "../../components/ThemedText";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import CommonNavBar from "../../components/CommonNavBar";
import { useLocation } from "react-router-dom";
import { imagesUrls } from "../../../constants";
import SectionTitle from "../../components/SectionTitle";

interface TransactionsHistoryProps {
  crypto?: string;
  withUserId?: string;
}

export const TransactionsHistory: React.FC<TransactionsHistoryProps> = ({
  crypto: cryptoFilter,
  withUserId,
}) => {
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const userData: UserModelData = useSelector(
    (state) => (state as any).actualUser
  );

  const actualUserId = userData?.user?.id;

  const fetchTransactions = async (
    lastDoc: any | null,
    refresh: boolean = false
  ) => {
    if (!actualUserId) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await arthereumProvider.getTransactionHistory(
        actualUserId,
        lastDoc,
        undefined,
        cryptoFilter,
        withUserId
      );
      setTransactions((prev) =>
        refresh ? result.transactions : [...prev, ...result.transactions]
      );
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load transactions"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (actualUserId) {
      setTransactions([]);
      setLastDoc(null);
      setHasMore(true);
      fetchTransactions(null, true);
    }
  }, [actualUserId, cryptoFilter]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setLastDoc(null);
    setHasMore(true);
    fetchTransactions(null, true);
  }, [actualUserId, cryptoFilter]);

  const handleLoadMore = () => {
    if (!loading && hasMore && actualUserId) {
      fetchTransactions(lastDoc);
    }
  };

  const renderTransaction = ({ item, index }: { item: any; index: any }) => {
    if (item?.type === "invitation_grant") {
      if (item?.to !== userData?.user?.id) {
        return null; // Return null to not render this item
      }
    }
    return (
      <div key={item.id} style={{ paddingLeft: "13px", paddingRight: "13px" }}>
        <div
          style={{
            backgroundColor: THEME_COLORS[theme].component_background_color,
            padding: 20,
            borderRadius: 12,
            position: "relative",
            overflow: "hidden",
            borderWidth: 1,
            borderColor: "rgba(74, 144, 226, 0.3)",
            marginBottom: 10,
            transition: "all 300ms ease",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <p
              style={{
                fontSize: 12,
                color:
                  item?.revoked !== true
                    ? THEME_COLORS[theme].inverse_theme
                    : THEME_COLORS[theme].red,
              }}
            >
              {item?.revoked === true ? getTranslationText("revoked") : ""}-
              {getTranslationText(item?.type)}-{item.id}
            </p>
            <img
              src={
                item?.crypto === "picoin"
                  ? imagesUrls["pi"]
                  : imagesUrls["arthereum"]
              }
              alt="Seal"
              style={{ width: 30, height: 30 }}
            />
          </div>
          <p
            style={{
              fontSize: 18,
              fontWeight: "600",
              marginBottom: 16,
              color: "#4a90e2",
            }}
          >
            {getTranslationText("amount_2", {
              amount:
                item?.type === "transfer"
                  ? item?.amount?.toFixed(2)
                  : item.grantValue?.toFixed(2),
            })}
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <p
              style={{
                fontSize: 14,
                color: THEME_COLORS[theme].inverse_theme,
              }}
            >
              {getTranslationText("date")}{" "}
              {new Date(item.date).toLocaleDateString()}
            </p>
            <p
              style={{
                fontSize: 14,
                color: THEME_COLORS[theme].inverse_theme,
              }}
            >
              {item?.crypto ?? "$ATH"} {item.grantValue?.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {error ? (
        <div
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <p
            style={{
              fontSize: 16,
              textAlign: "center",
              color: THEME_COLORS[theme].inverse_theme,
            }}
          >
            {error}
          </p>
        </div>
      ) : (
        <FlatList
          pageKey="TransactionsHistory"
          itemsClassName="Transaction"
          data={transactions}
          renderItems={renderTransaction}
          itemIdExtractor={(item: any) => item.id}
          onRefresh={handleRefresh}
          onEndReached={handleLoadMore}
          listHeaderComponents={
            <div style={{ paddingLeft: "10px", paddingRight: "10px" }}>
              <SectionTitle text={getTranslationText("transactions_hystory")} />
            </div>
          }
          listFooterComponents={
            <>
              {loading && !refreshing ? <LoadingIndicator /> : null}
              {!loading && transactions?.length === 0 ? (
                <div>
                  <Text
                    text={getTranslationText("no_transactions")}
                    style={{ color: THEME_COLORS[theme].inverse_theme }}
                  ></Text>
                </div>
              ) : null}
              <div style={{ height: "100px" }}></div>
            </>
          }
          // contentContainerStyle={styles.listContainer}
        />
      )}
    </>
  );
};

const HistoryPage = ({}) => {
  const theme = useThemeHook();
  const location = useLocation();
  const { getCryptoTransactions, crypto } = location.state ?? {};
  const getTranslationText = useTranslationHook();

  return (
    <div
      style={{
        backgroundColor: THEME_COLORS[theme].page_background_color,
      }}
    >
      <CommonNavBar />
      <TransactionsHistory
        crypto={getCryptoTransactions ? crypto : undefined}
      />
    </div>
  );
};

export default HistoryPage;
